// src/pages/inventory/index.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumbs from "../../components/ui/Breadcrumbs";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Select from "../../components/ui/Select";
import { Checkbox } from "../../components/ui/Checkbox";

import Icon from "../../components/AppIcon";

import api from "../../lib/api";
import {
  listProducts,
  createProduct,
  patchProduct,
  listStockLevels,
  listStockMovements,
  receiveStock,
  adjustStock,
} from "../../lib/inventory";

import { useAuth } from "../../auth/AuthProvider";

const fmtMoney = (val) =>
  new Intl.NumberFormat("ro-RO", { style: "currency", currency: "RON" }).format(
    Number(val || 0) || 0
  );

const fmtQty = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return "0";
  // max 1 zecimală, format RO (virgulă)
  return new Intl.NumberFormat("ro-RO", { maximumFractionDigits: 1 }).format(n);
};

const round1 = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return "";
  return String(Math.round(n * 10) / 10);
};

export default function InventoryPage() {
  const { t } = useTranslation();
  const { currentBusinessId } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // locations
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState(() => {
    if (typeof window === "undefined") return "";
    return String(window.localStorage.getItem("current_location_id") || "");
  });

  // products
  const [products, setProducts] = useState([]);

  // stock
  const [levels, setLevels] = useState([]);
  const [movements, setMovements] = useState([]);

  // forms
  const [newName, setNewName] = useState("");
  const [newUnit, setNewUnit] = useState("buc");
  const [newCost, setNewCost] = useState("");
  const [newTrack, setNewTrack] = useState(true);

  const [recvProductId, setRecvProductId] = useState("");
  const [recvQty, setRecvQty] = useState("");
  const [recvCost, setRecvCost] = useState("");

  const [adjProductId, setAdjProductId] = useState("");
  const [adjDelta, setAdjDelta] = useState("");
  const [adjCost, setAdjCost] = useState("");

  const breadcrumbItems = useMemo(
    () => [
      { label: t("common.home"), path: "/app" },
      { label: t("inventory.title"), path: "/app/inventory" },
    ],
    [t]
  );

  const locationOptions = useMemo(() => {
    const opts = (locations || []).map((l) => ({
      value: String(l.id),
      label: l.name || `Locație #${l.id}`,
    }));
    return opts;
  }, [locations]);

  const productOptions = useMemo(() => {
    return (products || [])
      .filter((p) => p?.is_active !== false)
      .map((p) => ({
        value: String(p.id),
        label: p.name || `Produs #${p.id}`,
      }));
  }, [products]);

  const loadLocations = async () => {
    const { data } = await api.get("/b/locations");
    const list = Array.isArray(data) ? data : [];
    setLocations(list);

    // auto-select: dacă n-ai locationId sau nu e valid, ia prima
    const ids = new Set(list.map((x) => String(x.id)));
    if (!locationId || !ids.has(String(locationId))) {
      const first = list?.[0]?.id != null ? String(list[0].id) : "";
      setLocationId(first);
      if (typeof window !== "undefined") {
        if (first) window.localStorage.setItem("current_location_id", first);
      }
    }
  };

  const reloadAll = async ({ silent = false } = {}) => {
    if (!currentBusinessId) return;
    if (!silent) setLoading(true);
    setErr("");

    try {
      const [p, lv, mv] = await Promise.all([
        listProducts(),
        listStockLevels({ locationId: locationId || undefined }),
        listStockMovements({ locationId: locationId || undefined, limit: 30 }),
      ]);

      setProducts(Array.isArray(p) ? p : []);
      setLevels(Array.isArray(lv) ? lv : []);
      setMovements(Array.isArray(mv) ? mv : []);
    } catch (e) {
      console.error(e);
      setErr(
        e?.response?.data?.detail ||
        e?.message ||
        t("inventory.loadError")
      );
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // bootstrap
  useEffect(() => {
    if (!currentBusinessId) return;

    let cancelled = false;

    const boot = async () => {
      setLoading(true);
      setErr("");
      try {
        await loadLocations();
        if (cancelled) return;
        await reloadAll({ silent: true });
      } catch (e) {
        console.error(e);
        setErr(
          e?.response?.data?.detail ||
          e?.message ||
          t("inventory.loadError")
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    boot();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBusinessId]);

  // când schimb locația, refacem nivelurile + mișcările
  useEffect(() => {
    if (!currentBusinessId) return;
    reloadAll({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  const handleCreateProduct = async (e) => {
    e?.preventDefault?.();
    const name = String(newName || "").trim();
    if (!name) return;

    try {
      setLoading(true);
      setErr("");

      const default_cost = newCost === "" ? null : Number(newCost);

      await createProduct({
        name,
        unit: String(newUnit || "buc").trim() || "buc",
        track_stock: Boolean(newTrack),
        default_cost: default_cost,
        is_active: true
      });

      setNewName("");
      setNewUnit("buc");
      setNewCost("");
      setNewTrack(true);

      await reloadAll({ silent: true });
    } catch (e2) {
      console.error(e2);
      setErr(e2?.response?.data?.detail || e2?.message || t("inventory.createError"));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleProduct = async (product, key) => {
    if (!product?.id) return;
    const next = !Boolean(product?.[key]);
    try {
      setErr("");
      const payload = { [key]: next };
      await patchProduct(product.id, payload);
      await reloadAll({ silent: true });
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.detail || e?.message || "Eroare la actualizarea produsului");
    }
  };

  const handleReceive = async (e) => {
    e?.preventDefault?.();
    if (!recvProductId || !locationId) return;

    const qty = Number(round1(recvQty));
    if (!Number.isFinite(qty) || qty <= 0) return;

    const unit_cost = recvCost === "" ? null : Number(recvCost);

    try {
      setLoading(true);
      setErr("");
      await receiveStock({
        product_id: Number(recvProductId),
        location_id: Number(locationId),
        qty: qty,
        unit_cost: unit_cost,
      });

      setRecvQty("");
      setRecvCost("");
      await reloadAll({ silent: true });
    } catch (e2) {
      console.error(e2);
      setErr(e2?.response?.data?.detail || e2?.message || t("inventory.receiveError"));
    } finally {
      setLoading(false);
    }
  };

  const handleAdjust = async (e) => {
    e?.preventDefault?.();
    if (!adjProductId || !locationId) return;

    const qty_delta = Number(round1(adjDelta));
    if (!Number.isFinite(qty_delta) || qty_delta === 0) return;

    const unit_cost = adjCost === "" ? null : Number(adjCost);

    try {
      setLoading(true);
      setErr("");
      await adjustStock({
        product_id: Number(adjProductId),
        location_id: Number(locationId),
        qty_delta: qty_delta,
        unit_cost: unit_cost,
      });

      setAdjDelta("");
      setAdjCost("");
      await reloadAll({ silent: true });
    } catch (e2) {
      console.error(e2);
      setErr(e2?.response?.data?.detail || e2?.message || t("inventory.adjustError"));
    } finally {
      setLoading(false);
    }
  };

  const levelsByProduct = useMemo(() => {
    const m = new Map();
    for (const row of levels || []) {
      const pid = row?.product_id ?? row?.product?.id ?? row?.product;
      if (pid == null) continue;
      m.set(String(pid), row);
    }
    return m;
  }, [levels]);

  const productNameById = useMemo(() => {
    const m = new Map();
    for (const p of products || []) m.set(String(p.id), p?.name || `Produs #${p.id}`);
    return m;
  }, [products]);

  return (
    <div className="min-h-screen bg-background">
      <Header isCollapsed={sidebarCollapsed} onToggleSidebar={() => setSidebarCollapsed((v) => !v)} />
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed((v) => !v)} />

      <main
        className={`transition-all duration-300 ease-out-custom page-top pb-20 lg:pb-6 ${sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
          }`}
      >
        <div className="p-4 sm:p-6">
          <div className="mb-8">
            <Breadcrumbs items={breadcrumbItems} className="mb-4" />

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">{t("inventory.title")}</h1>
                <p className="text-text-secondary">
                  {t("inventory.subtitle")}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="w-full sm:w-auto sm:min-w-[260px]">
                  <Select
                    options={locationOptions}
                    value={locationId}
                    onChange={(v) => setLocationId(String(v || ""))}
                    placeholder={t("inventory.selectLocation")}
                    searchable
                    clearable={false}
                  />
                </div>

                <Button
                  variant="outline"
                  onClick={() => reloadAll()}
                  disabled={loading}
                >
                  <Icon name="RefreshCcw" size={16} className="mr-2" />
                  {t("inventory.reload")}
                </Button>
              </div>
            </div>
          </div>

          {err && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {err}
            </div>
          )}

          {loading && <div className="mb-4 text-sm text-text-secondary">{t("common.loading")}</div>}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Products */}
            <div className="sm:bg-surface sm:border sm:border-border sm:rounded-xl p-0 sm:p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="font-semibold text-foreground">{t("inventory.products")}</div>
                <div className="text-xs text-text-secondary">{products?.length || 0} {t("common.total").toLowerCase()}</div>
              </div>

              {/* Add product */}
              <form onSubmit={handleCreateProduct} className="space-y-3 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input
                    placeholder={t("inventory.placeholderName")}
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                  <Input
                    placeholder={t("inventory.placeholderUnit")}
                    value={newUnit}
                    onChange={(e) => setNewUnit(e.target.value)}
                  />
                  <Input
                    placeholder={t("inventory.placeholderCost")}
                    value={newCost}
                    onChange={(e) => setNewCost(e.target.value)}
                    type="number"
                    step="0.01"
                  />
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <Checkbox
                    checked={newTrack}
                    onChange={(e) => setNewTrack(e.target.checked)}
                    label={t("inventory.trackStock")}
                    description={t("inventory.trackStockDesc")}
                  />

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full sm:w-auto flex-shrink-0"
                  >
                    <Icon name="Plus" size={16} className="mr-2" />
                    {t("common.add")}
                  </Button>
                </div>
              </form>

              {/* Mobile: listă verticală (stacked) */}
              <div className="sm:hidden">
                <div className="border-t border-border mt-4 pt-4 mb-3 text-sm font-medium text-text-secondary">
                  {t("inventory.existingProducts")} ({products?.length || 0})
                </div>

                <div className="space-y-4">
                  {(products || []).map((p) => {
                    const row = levelsByProduct.get(String(p.id));
                    const qty = row?.qty_on_hand ?? row?.qty ?? 0;
                    const avg = row?.avg_cost ?? row?.unit_cost ?? p?.default_cost ?? 0;

                    return (
                      <div
                        key={p.id}
                        className="border-b border-border pb-4 last:border-b-0 last:pb-0"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="min-w-0">
                            <div className="font-medium text-foreground break-words">{p.name}</div>
                            {p.sku ? (
                              <div className="text-xs text-text-secondary">SKU: {p.sku}</div>
                            ) : null}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                              type="button"
                              variant={p.is_active === false ? "outline" : "default"}
                              size="sm"
                              onClick={() => handleToggleProduct(p, "is_active")}
                            >
                              {p.is_active === false ? t("common.inactive") : t("common.active")}
                            </Button>
                            <Button
                              type="button"
                              variant={p.track_stock === false ? "outline" : "ghost"}
                              size="sm"
                              onClick={() => handleToggleProduct(p, "track_stock")}
                              title={t("inventory.trackStock")}
                            >
                              <Icon name={p.track_stock === false ? "EyeOff" : "Eye"} size={16} />
                            </Button>
                          </div>
                        </div>
                        <dl className="text-sm">
                          <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-border last:border-b-0">
                            <dt className="text-text-secondary">{t("inventory.unit")}</dt>
                            <dd className="text-foreground font-medium text-right">{p.unit || "-"}</dd>
                          </div>
                          <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-border last:border-b-0">
                            <dt className="text-text-secondary">{t("common.price")}</dt>
                            <dd className="text-foreground font-medium text-right">
                              {fmtMoney(p.default_cost ?? 0)}
                              {avg != null ? (
                                <span className="text-text-secondary font-normal"> · avg {fmtMoney(avg)}</span>
                              ) : null}
                            </dd>
                          </div>
                          <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-border last:border-b-0">
                            <dt className="text-text-secondary">{t("inventory.stock")}</dt>
                            <dd className="text-foreground font-medium text-right">{fmtQty(qty)}</dd>
                          </div>
                        </dl>
                      </div>
                    );
                  })}

                  {(!products || products.length === 0) && (
                    <div className="py-4 text-text-secondary text-sm">
                      {t("inventory.noProducts")}
                    </div>
                  )}
                </div>
              </div>

              {/* Desktop: tabel */}
              <div className="hidden sm:block overflow-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-text-secondary border-b border-border">
                      <th className="py-2 pr-2">{t("inventory.product")}</th>
                      <th className="py-2 pr-2">{t("inventory.unit")}</th>
                      <th className="py-2 pr-2">{t("common.price")}</th>
                      <th className="py-2 pr-2">{t("inventory.stock")}</th>
                      <th className="py-2 pr-2">{t("common.active")}</th>
                      <th className="py-2 pr-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(products || []).map((p) => {
                      const row = levelsByProduct.get(String(p.id));
                      const qty = row?.qty_on_hand ?? row?.qty ?? 0;
                      const avg = row?.avg_cost ?? row?.unit_cost ?? p?.default_cost ?? 0;

                      return (
                        <tr key={p.id} className="border-b border-border last:border-b-0">
                          <td className="py-2 pr-2">
                            <div className="font-medium text-foreground">{p.name}</div>
                            {p.sku ? (
                              <div className="text-xs text-text-secondary">SKU: {p.sku}</div>
                            ) : null}
                          </td>
                          <td className="py-2 pr-2 text-text-secondary">{p.unit || "-"}</td>
                          <td className="py-2 pr-2 text-text-secondary">
                            {fmtMoney(p.default_cost ?? 0)}
                            {avg != null ? (
                              <div className="text-xs">avg: {fmtMoney(avg)}</div>
                            ) : null}
                          </td>
                          <td className="py-2 pr-2 text-text-secondary">
                            {fmtQty(qty)}
                          </td>
                          <td className="py-2 pr-2">
                            <Button
                              type="button"
                              variant={p.is_active === false ? "outline" : "default"}
                              size="sm"
                              onClick={() => handleToggleProduct(p, "is_active")}
                            >
                              {p.is_active === false ? t("common.inactive") : t("common.active")}
                            </Button>
                          </td>
                          <td className="py-2 pr-2">
                            <Button
                              type="button"
                              variant={p.track_stock === false ? "outline" : "ghost"}
                              size="sm"
                              onClick={() => handleToggleProduct(p, "track_stock")}
                              title={t("inventory.trackStock")}
                            >
                              <Icon name={p.track_stock === false ? "EyeOff" : "Eye"} size={16} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}

                    {(!products || products.length === 0) && (
                      <tr>
                        <td colSpan={6} className="py-4 text-text-secondary">
                          {t("inventory.noProducts")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Stock ops + movements */}
            <div className="space-y-6">
              <div className="border-t border-border pt-6 sm:border-t-0 sm:pt-0 sm:bg-surface sm:border sm:border-border sm:rounded-xl p-0 sm:p-4">
                <div className="font-semibold text-foreground mb-4">{t("inventory.receiveAdjust")}</div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <form onSubmit={handleReceive} className="space-y-3">
                    <div className="text-sm font-medium text-foreground">{t("inventory.receiveGoods")}</div>
                    <Select
                      options={productOptions}
                      value={recvProductId}
                      onChange={(v) => setRecvProductId(String(v || ""))}
                      placeholder={t("inventory.product")}
                      searchable
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        placeholder={t("inventory.placeholderQty")}
                        value={recvQty}
                        onChange={(e) => setRecvQty(e.target.value)}
                        onBlur={(e) => setRecvQty(round1(e.target.value))}
                        type="number"
                        step="0.1"
                      />

                      <Input
                        placeholder={t("inventory.placeholderCostUnit")}
                        value={recvCost}
                        onChange={(e) => setRecvCost(e.target.value)}
                        type="number"
                        step="0.01"
                      />
                    </div>
                    <Button type="submit" disabled={loading || !recvProductId || !locationId}>
                      <Icon name="ArrowDownToLine" size={16} className="mr-2" />
                      {t("inventory.receive")}
                    </Button>
                    <div className="text-xs text-text-secondary">
                      {t("inventory.receiveDesc")}
                    </div>
                  </form>

                  <form onSubmit={handleAdjust} className="space-y-3">
                    <div className="text-sm font-medium text-foreground">{t("inventory.adjustStock")}</div>
                    <Select
                      options={productOptions}
                      value={adjProductId}
                      onChange={(v) => setAdjProductId(String(v || ""))}
                      placeholder={t("inventory.product")}
                      searchable
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        placeholder={t("inventory.placeholderDelta")}
                        value={adjDelta}
                        onChange={(e) => setAdjDelta(e.target.value)}
                        onBlur={(e) => setAdjDelta(round1(e.target.value))}
                        type="number"
                        step="0.1"
                      />
                      <Input
                        placeholder={t("inventory.placeholderCostOpt")}
                        value={adjCost}
                        onChange={(e) => setAdjCost(e.target.value)}
                        type="number"
                        step="0.01"
                      />
                    </div>
                    <Button type="submit" variant="outline" disabled={loading || !adjProductId || !locationId}>
                      <Icon name="PencilLine" size={16} className="mr-2" />
                      {t("inventory.applyAdjust")}
                    </Button>
                    <div className="text-xs text-text-secondary">
                      {t("inventory.adjustDesc")}
                    </div>
                  </form>
                </div>
              </div>

              <div className="border-t border-border pt-6 sm:border-t-0 sm:pt-0 sm:bg-surface sm:border sm:border-border sm:rounded-xl p-0 sm:p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="font-semibold text-foreground">{t("inventory.movements")}</div>
                  <div className="text-xs text-text-secondary">{t("inventory.last")} {movements?.length || 0}</div>
                </div>

                {/* Mobile: listă verticală (stacked) */}
                <div className="space-y-4 sm:hidden">
                  {(movements || []).map((m) => {
                    const created = m.created_at || m.createdAt || m.date || "";
                    const pid = m.product_id ?? m.product?.id ?? m.product;
                    const name = pid != null ? productNameById.get(String(pid)) : "-";
                    const delta = m.qty_delta ?? m.delta ?? 0;
                    const cost = m.unit_cost ?? m.unitCost ?? null;
                    const reason = m.reason || m.type || "";

                    return (
                      <div
                        key={m.id || `${created}-${pid}-${delta}`}
                        className="border-b border-border pb-4 last:border-b-0 last:pb-0"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="font-medium text-foreground break-words min-w-0">
                            {name || "-"}
                          </div>
                          <span
                            className={`flex-shrink-0 font-medium ${Number(delta) < 0 ? "text-red-500" : "text-green-500"
                              }`}
                          >
                            {fmtQty(delta)}
                          </span>
                        </div>
                        <dl className="text-sm">
                          <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-border last:border-b-0">
                            <dt className="text-text-secondary">{t("common.date")}</dt>
                            <dd className="text-foreground text-right">
                              {created ? new Date(created).toLocaleString("ro-RO") : "-"}
                            </dd>
                          </div>
                          <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-border last:border-b-0">
                            <dt className="text-text-secondary">{t("inventory.costPerUnit")}</dt>
                            <dd className="text-foreground text-right">
                              {cost == null ? "-" : fmtMoney(cost)}
                            </dd>
                          </div>
                          {reason ? (
                            <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-border last:border-b-0">
                              <dt className="text-text-secondary">{t("common.description")}</dt>
                              <dd className="text-foreground text-right break-words">{reason}</dd>
                            </div>
                          ) : null}
                        </dl>
                      </div>
                    );
                  })}

                  {(!movements || movements.length === 0) && (
                    <div className="py-4 text-text-secondary text-sm">
                      {t("inventory.noMovements")}
                    </div>
                  )}
                </div>

                {/* Desktop: tabel */}
                <div className="hidden sm:block overflow-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-text-secondary border-b border-border">
                        <th className="py-2 pr-2">{t("common.date")}</th>
                        <th className="py-2 pr-2">{t("inventory.product")}</th>
                        <th className="py-2 pr-2">Delta</th>
                        <th className="py-2 pr-2">{t("inventory.costPerUnit")}</th>
                        <th className="py-2 pr-2">{t("common.description")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(movements || []).map((m) => {
                        const created = m.created_at || m.createdAt || m.date || "";
                        const pid = m.product_id ?? m.product?.id ?? m.product;
                        const name = pid != null ? productNameById.get(String(pid)) : "-";
                        const delta = m.qty_delta ?? m.delta ?? 0;
                        const cost = m.unit_cost ?? m.unitCost ?? null;
                        const reason = m.reason || m.type || "";

                        return (
                          <tr key={m.id || `${created}-${pid}-${delta}`} className="border-b border-border last:border-b-0">
                            <td className="py-2 pr-2 text-text-secondary">
                              {created ? new Date(created).toLocaleString("ro-RO") : "-"}
                            </td>
                            <td className="py-2 pr-2 text-foreground">{name || "-"}</td>
                            <td className="py-2 pr-2">
                              <span className={Number(delta) < 0 ? "text-red-500" : "text-green-500"}>
                                {fmtQty(delta)}
                              </span>
                            </td>
                            <td className="py-2 pr-2 text-text-secondary">
                              {cost == null ? "-" : fmtMoney(cost)}
                            </td>
                            <td className="py-2 pr-2 text-text-secondary">{reason || "-"}</td>
                          </tr>
                        );
                      })}

                      {(!movements || movements.length === 0) && (
                        <tr>
                          <td colSpan={5} className="py-4 text-text-secondary">
                            {t("inventory.noMovements")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="mt-3 text-xs text-text-secondary">
                  Dacă schimbi o programare completed / anulare după completed, backend-ul trebuie să facă reversals (ledger). UI-ul de aici doar arată rezultatul.
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
